import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronDown, FiFilter, FiGrid, FiList, FiX } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ProductCard } from '../components/ProductCard';
import { getActiveProducts, FrontendProduct } from '../api/productApi';
import PriceRangeSlider from '../components/PriceRangeSlider';
import CategoryFilter, { standardCategories, categorySizeTypes } from '../components/CategoryFilter';

// Update the Product type to include isFeatured and colorway
interface Product {
  id: string | number;
  name: string;
  brand: string;
  price: number;
  image: string;
  images?: string[];
  sizes: string[];
  category?: string;
  isFeatured?: boolean;
  condition?: string;
  sizeDisplay?: string;
  sizeType?: string;
  colorway?: string;
}

const EntireInventoryPage: React.FC = () => {
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleProductCount, setVisibleProductCount] = useState(12);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSize, setSelectedSize] = useState("All");
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(15000);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const unmountingRef = useRef(false);
  
  // Filter options - kept static as these are logical groupings, removed Best Sellers
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'featured', label: 'Featured' },
    { id: 'new', label: 'New Arrivals' }
  ];
  
  // Size category options - kept static as these are defined size types
  const categories = [
    { id: 'All', label: 'All Categories' },
    { id: 'M', label: "Men's (M)" },
    { id: 'W', label: "Women's (W)" },
    { id: 'TD', label: 'Toddlers (TD)' },
    { id: 'GS', label: 'Grade School (GS)' }
  ];
  
  // Brand options - dynamically generated from products
  const generateBrandOptions = () => {
    // Always include "All Brands" option
    const options = [{ id: 'All', label: 'All Brands' }];
    
    // Get unique brands from products
    const uniqueBrands = Array.from(new Set(products.map(product => product.brand)));
    
    // Sort brands alphabetically
    uniqueBrands.sort();
    
    // Add each unique brand to options
    uniqueBrands.forEach(brand => {
      if (brand && brand.trim() !== '') {
        options.push({ id: brand, label: brand });
      }
    });
    
    return options;
  };
  
  // Generate brand options based on available products
  const brands = generateBrandOptions();
  
  // Get dynamic size options based on current category and available products
  const getDynamicSizeOptions = (category: string) => {
    const options = ['All']; // Always include "All" option
    
    console.log(`Getting size options for category: ${category}`);
    
    // Filter products by the selected category
    const filteredProducts = category === 'All' 
      ? products 
      : products.filter(product => {
          // Check if the category matches the product's sizeType
          const match = (product.sizeType && product.sizeType.includes(category)) ||
                      (product.category === category);
          
          if (!match) {
            console.log(`Product ${product.id} excluded from size options - need: ${category}, has sizeType: ${product.sizeType}, category: ${product.category}`);
          } else {
            console.log(`Product ${product.id} included for size options - matches category: ${category}`);
          }
          
          return match;
        });
    
    console.log(`Found ${filteredProducts.length} products matching category ${category}`);
    
    // Get all available sizes from filtered products
    const allSizes: string[] = [];
    const numericSizesMap: { [key: string]: string } = {}; // To track numeric sizes and their original formats
    
    filteredProducts.forEach(product => {
      console.log(`Processing sizes for product ${product.id}: ${JSON.stringify(product.sizes)}`);
      
      product.sizes.forEach(size => {
        // Add the original size format
        if (!allSizes.includes(size)) {
          allSizes.push(size);
          console.log(`Added size ${size} to available sizes`);
          
          // Track numeric representation for potential deduplication
          if (size.match(/[0-9]/)) {
            let numericPart = "";
            
            // Extract the numeric part of the size
            if (size.includes('M')) {
              numericPart = size.replace('M', '');
            } else if (size.includes('Y')) {
              numericPart = size.replace('Y', '');
            } else if (size.includes('W')) {
              numericPart = size.replace('W', '');
            } else if (!isNaN(parseFloat(size))) {
              numericPart = size;
            }
            
            // If we have a clear numeric part, remember the original format
            if (numericPart && !isNaN(parseFloat(numericPart))) {
              numericSizesMap[numericPart] = size;
            }
          }
        }
      });
    });
    
    console.log(`After processing all products, found ${allSizes.length} unique sizes`);
    
    // Return early with just "All" if no sizes found
    if (allSizes.length === 0) {
      console.log(`No sizes found for category ${category}, returning only "All" option`);
      return options;
    }
    
    // For categories like Men's, prioritize numeric sizes (e.g., "9" over "9M")
    if (category === 'M') {
      // Add numeric sizes without suffix if not already present
      Object.keys(numericSizesMap).forEach(numericSize => {
        const originalSize = numericSizesMap[numericSize];
        if (originalSize.includes('M') && !allSizes.includes(numericSize)) {
          allSizes.push(numericSize);
        }
      });
    }
    
    // For GS category, prioritize numeric sizes (e.g., "4" over "4Y")
    if (category === 'GS') {
      // Add numeric sizes without suffix if not already present
      Object.keys(numericSizesMap).forEach(numericSize => {
        const originalSize = numericSizesMap[numericSize];
        if (originalSize.includes('Y') && !allSizes.includes(numericSize)) {
          allSizes.push(numericSize);
        }
      });
    }
    
    // Sort sizes numerically if possible
    allSizes.sort((a, b) => {
      // If sizes contain letters, use a more complex sorting
      if (a.match(/[a-zA-Z]/) || b.match(/[a-zA-Z]/)) {
        // Extract numeric parts for comparison
        const numA = parseFloat(a.replace(/[a-zA-Z]/g, ''));
        const numB = parseFloat(b.replace(/[a-zA-Z]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }
      // Otherwise, sort numerically
      return parseFloat(a) - parseFloat(b);
    });
    
    return [...options, ...allSizes];
  };
  
  // Generate dynamic price ranges based on available products
  const generatePriceRanges = () => {
    // Always include "All Prices" option
    const options = [{ id: 'All', label: 'All Prices' }];
    
    // Standard price ranges
    const standardRanges = [
      { id: '0-100', label: 'Under $100' },
      { id: '100-200', label: '$100 - $200' },
      { id: '200-300', label: '$200 - $300' },
      { id: '300-500', label: '$300 - $500' },
      { id: '500+', label: 'Over $500' }
    ];
    
    // Only include price ranges that have products
    standardRanges.forEach(range => {
      const [min, max] = range.id.split('-').map(Number);
      const hasProductsInRange = products.some(product => {
        if (!max) {
          // Handle "500+" case
          return product.price >= min;
        }
        return product.price >= min && product.price <= max;
      });
      
      if (hasProductsInRange) {
        options.push(range);
      }
    });
    
    return options;
  };
  
  // Generate price range options based on available products
  const priceRanges = generatePriceRanges();
  
  useEffect(() => {
    return () => {
      // Mark component as unmounting to prevent state updates
      unmountingRef.current = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        console.log("Fetching products from API...");
        // Use getActiveProducts from the API
        const data = await getActiveProducts();
        
        // Don't update state if component is unmounting
        if (!isMounted || unmountingRef.current) return;
        
        console.log(`API returned ${data.length} products`);
        
        // Transform FrontendProduct to Product type if needed
        const formattedProducts: Product[] = data.map(product => {
          // Create a sizeDisplay if it's missing
          let sizeDisplay = product.sizeDisplay;
          if (!sizeDisplay && product.size) {
            const sizeType = product.sizeType || '';
            if (sizeType === 'M' || sizeType === 'W') {
              sizeDisplay = `${product.size}${sizeType}`;
            } else if (sizeType === 'TD' || sizeType === 'GS' || sizeType.includes('Y') || sizeType.includes('C')) {
              sizeDisplay = `${product.size} (${sizeType})`;
            } else {
              // If sizeType is unknown but we can infer from category
              const category = product.sizeType?.includes('M') ? 'M' : 
                             product.sizeType?.includes('W') ? 'W' : 
                             product.sizeType?.includes('Y') ? 'GS' : 
                             product.sizeType?.includes('C') ? 'TD' : null;
              
              if (category) {
                if (category === 'M' || category === 'W') {
                  sizeDisplay = `${product.size}${category}`;
                } else {
                  sizeDisplay = `${product.size} (${category})`;
                }
              } else {
                // Last resort - just use the size as is
                sizeDisplay = product.size;
              }
            }
          }
          
          // Ensure sizes array is properly populated
          const sizes = product.sizes && product.sizes.length > 0 
            ? product.sizes 
            : product.size 
              ? [product.size] 
              : [];
          
          console.log(`Processing product ${product.id} (${product.name}): size=${product.size}, sizes=${JSON.stringify(sizes)}, sizeType=${product.sizeType}, sizeDisplay=${sizeDisplay}`);
          
          return {
            id: product.id,
            name: product.name,
            brand: product.brand,
            price: product.price,
            image: product.image,
            images: product.images || (product.image ? [product.image] : []),
            sizes: sizes,
            category: product.sizeType?.includes('M') ? 'M' : 
                     product.sizeType?.includes('W') ? 'W' : 
                     product.sizeType?.includes('Y') ? 'GS' : 
                     product.sizeType?.includes('C') ? 'TD' : 'All',
            condition: product.condition,
            sizeDisplay: sizeDisplay,
            sizeType: product.sizeType
          };
        });
        
        if (isMounted && !unmountingRef.current) {
          setProducts(formattedProducts);
          
          setLoading(false);
          console.log("Products loaded successfully");
        }
      } catch (e) {
        console.error("Error fetching products:", e);
        if (isMounted && !unmountingRef.current) {
          setError('Failed to load products');
          setLoading(false);
        }
      }
    };
    
    fetchProducts();
    
    return () => {
      isMounted = false;
      setLoading(false); // Ensure loading state is reset
    };
  }, []);
  
  // Listen for product inventory changes (e.g., after successful checkout)
  useEffect(() => {
    const handleInventoryChange = () => {
      // Only refresh if component is still mounted and not loading
      if (!unmountingRef.current && !loading) {
        const fetchProducts = async () => {
          try {
            setLoading(true);
            const data = await getActiveProducts();
            
            if (unmountingRef.current) return;
            
            const formattedProducts: Product[] = data.map(product => {
              // ... existing formatting logic
              let sizeDisplay = product.sizeDisplay;
              if (!sizeDisplay && product.size) {
                const sizeType = product.sizeType || '';
                if (sizeType === 'M' || sizeType === 'W') {
                  sizeDisplay = `${product.size}${sizeType}`;
                } else if (sizeType === 'TD' || sizeType === 'GS' || sizeType.includes('Y') || sizeType.includes('C')) {
                  sizeDisplay = `${product.size} (${sizeType})`;
                } else {
                  const category = product.sizeType?.includes('M') ? 'M' : 
                                 product.sizeType?.includes('W') ? 'W' : 
                                 product.sizeType?.includes('Y') ? 'GS' : 
                                 product.sizeType?.includes('C') ? 'TD' : null;
                  
                  if (category) {
                    if (category === 'M' || category === 'W') {
                      sizeDisplay = `${product.size}${category}`;
                    } else {
                      sizeDisplay = `${product.size} (${category})`;
                    }
                  } else {
                    sizeDisplay = product.size;
                  }
                }
              }
              
              const sizes = product.sizes && product.sizes.length > 0 
                ? product.sizes 
                : product.size 
                  ? [product.size] 
                  : [];
              
              return {
                id: product.id,
                name: product.name,
                brand: product.brand,
                price: product.price,
                image: product.image,
                images: product.images || (product.image ? [product.image] : []),
                sizes: sizes,
                category: product.sizeType?.includes('M') ? 'M' : 
                         product.sizeType?.includes('W') ? 'W' : 
                         product.sizeType?.includes('Y') ? 'GS' : 
                         product.sizeType?.includes('C') ? 'TD' : 'All',
                condition: product.condition,
                sizeDisplay: sizeDisplay,
                sizeType: product.sizeType
              };
            });
            
            if (!unmountingRef.current) {
              setProducts(formattedProducts);
              setLoading(false);
            }
          } catch (e) {
            console.error("Error refreshing products:", e);
            if (!unmountingRef.current) {
              setLoading(false);
            }
          }
        };
        
        fetchProducts();
      }
    };
    
    window.addEventListener('productInventoryChanged', handleInventoryChange);
    
    return () => {
      window.removeEventListener('productInventoryChanged', handleInventoryChange);
    };
  }, [loading]); // Depend on loading to avoid conflicts
  
  // Reset the size when category changes
  useEffect(() => {
    setSelectedSize('All');
  }, [selectedCategory]);
  
  // Helper function to normalize size display for UI consistency
  const normalizeSizeDisplay = (size: string): string => {
    // If the size is numeric only (e.g., "9"), check the current category
    if (!isNaN(parseFloat(size)) && !size.match(/[a-zA-Z]/)) {
      // Add category suffix based on the current selection
      if (selectedCategory === 'M') return `${size}M`;
      if (selectedCategory === 'W') return `${size}W`;
      if (selectedCategory === 'GS') return `${size}Y`;
      // Default to Men's if no category is selected
      return selectedCategory === 'All' ? `${size}M` : `${size}M`;
    }
    return size;
  };

  // Get current applicable sizes based on category
  const getCurrentSizes = () => {
    console.log("Getting sizes for category:", selectedCategory);
    const sizes = getDynamicSizeOptions(selectedCategory);
    
    // If we have a size parameter in the URL, ensure it's in the dropdown
    // Either as is or as a numeric version
    if (selectedSize !== 'All') {
      // If the exact selected size isn't in the options, add it
      if (!sizes.includes(selectedSize)) {
        console.log(`Adding selected size ${selectedSize} to dropdown options`);
        sizes.push(selectedSize);
      }
      
      // Also ensure we have the stripped version if the selection has a suffix
      if (selectedSize.match(/[a-zA-Z]/)) {
        const strippedSize = stripSizeSuffix(selectedSize);
        if (strippedSize && !sizes.includes(strippedSize)) {
          console.log(`Adding stripped size ${strippedSize} to dropdown options`);
          sizes.push(strippedSize);
        }
      }
      
      // Sort sizes to maintain order
      sizes.sort((a, b) => {
        if (a === 'All') return -1;
        if (b === 'All') return 1;
        
        // Extract numeric parts for comparison
        const numA = parseFloat(a.replace(/[a-zA-Z]/g, ''));
        const numB = parseFloat(b.replace(/[a-zA-Z]/g, ''));
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    
    // Also ensure selectedSizes values are in the dropdown
    if (selectedSizes.length > 0) {
      let modified = false;
      
      // Process each size in selectedSizes
      selectedSizes.forEach(sizeValue => {
        // Add the exact size with suffix if not present
        if (!sizes.includes(sizeValue)) {
          console.log(`Adding selected size ${sizeValue} from selectedSizes array`);
          sizes.push(sizeValue);
          modified = true;
        }
        
        // Add the stripped version for numeric-only display
        if (sizeValue.match(/[a-zA-Z]/)) {
          const strippedSize = stripSizeSuffix(sizeValue);
          if (strippedSize && !sizes.includes(strippedSize)) {
            console.log(`Adding stripped size ${strippedSize} to dropdown options`);
            sizes.push(strippedSize);
            modified = true;
          }
        }
      });
      
      // Resort if we modified the array
      if (modified) {
        sizes.sort((a, b) => {
          if (a === 'All') return -1;
          if (b === 'All') return 1;
          
          // Extract numeric parts for comparison
          const numA = parseFloat(a.replace(/[a-zA-Z]/g, ''));
          const numB = parseFloat(b.replace(/[a-zA-Z]/g, ''));
          
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          
          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
    }
    
    console.log("Available sizes for dropdown:", sizes);
    return sizes;
  };
  
  // Helper function to strip suffixes for dropdown selection
  const stripSizeSuffix = (size: string): string => {
    // Strip M suffix for men's sizes
    if (size.includes('M')) {
      const numPart = parseFloat(size.replace('M', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    // Strip Y suffix for GS sizes
    if (size.includes('Y')) {
      const numPart = parseFloat(size.replace('Y', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    // Strip W suffix for women's sizes
    if (size.includes('W')) {
      const numPart = parseFloat(size.replace('W', ''));
      if (!isNaN(numPart)) return numPart.toString();
    }
    
    return size;
  };

  // Modify the useEffect that handles URL parameters to respect loading state
  useEffect(() => {
    if (loading || products.length === 0) {
      return; // Wait until products are loaded
    }
    
    const queryParams = new URLSearchParams(location.search);
    
    // Handle size filter from URL
    const sizeParam = queryParams.get('sizes');
    if (sizeParam) {
      console.log(`Processing URL size parameter: ${sizeParam} with ${products.length} products loaded`);
      const sizeList = sizeParam.split(',');
      
      // Determine if we have GS sizes or Men's sizes
      const hasGSSizes = sizeList.some(size => size.includes('Y'));
      const hasMenSizes = sizeList.some(size => size.includes('M'));
      const hasWomenSizes = sizeList.some(size => size.includes('W'));
      
      // Set the appropriate category
      let sizeCategoryValue = 'M'; // Default to Men's
      if (hasGSSizes) {
        sizeCategoryValue = 'GS';
      } else if (hasMenSizes) {
        sizeCategoryValue = 'M';
      } else if (hasWomenSizes) {
        sizeCategoryValue = 'W';
      }
      
      // Set category first to ensure proper dropdown options are available
      setSelectedCategory(sizeCategoryValue);
      
      // For single size parameter, use the dropdown filter
      if (sizeList.length === 1) {
        // Set a single specific size value in the dropdown
        const exactSize = sizeList[0];
        
        // Prepare to handle different size formats
        const strippedSize = stripSizeSuffix(exactSize);
        
        // Since the dropdown may not be immediately ready with the new category's options,
        // use a small timeout to ensure the category change has propagated
        setTimeout(() => {
          // Initialize available sizes
          const availableSizes = getDynamicSizeOptions(sizeCategoryValue);
          console.log("Available sizes for dropdown:", availableSizes);
          
          // For Shop By Size links with size suffix (e.g., 9M), we want to:
          // 1. Always use the full size with suffix in the selectedSizes array for filtering
          // 2. Use the stripped numeric size (e.g., 9) in the dropdown if available
          
          // Store the original size with suffix in selectedSizes for filtering
          setSelectedSizes([exactSize]);
          
          // For the dropdown selection, prefer the numeric version without suffix
          // if it exists in the available options
          if (strippedSize && availableSizes.includes(strippedSize)) {
            console.log(`Setting dropdown to numeric size: ${strippedSize}`);
            setSelectedSize(strippedSize);
          } 
          // If numeric version not available, try the exact size with suffix
          else if (availableSizes.includes(exactSize)) {
            console.log(`Setting dropdown to exact size: ${exactSize}`);
            setSelectedSize(exactSize);
          }
          // If neither option is in the dropdown, add the numeric version
          else {
            console.log(`Adding size ${strippedSize} to available options`);
            // Force the dropdown to include our size
            setSelectedSize(strippedSize || exactSize);
          }
        }, 100); // Small timeout to ensure category is set first
      } else {
        // For multiple sizes, use the selectedSizes array for filtering
        console.log(`Setting multiple sizes from URL: ${sizeList.join(', ')}`);
        setSelectedSizes(sizeList);
        // Clear the dropdown selection
        setSelectedSize('All');
      }
      
      // Reset other filters to show all products within the size range
      setSelectedBrand('All');
      setMinPrice(0);
      setMaxPrice(15000);
    }
  }, [location.search, loading, products]);
  
  // Add useEffect to handle search query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    
    if (searchQuery) {
      // If we have a search query parameter, apply search filtering
      console.log(`Searching for: ${searchQuery}`);
      
      // Reset other filters when searching
      setActiveFilter('all');
      setSelectedBrand('All');
      setSelectedCategory('All');
      setSelectedSize('All');
      
      // The search filtering is handled in the filtering logic below
    }
  }, [location.search]);
  
  // Add search filtering to the existing filter logic
  const getFilteredProducts = () => {
    // Get search query from URL
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    
    return products.filter(product => {
      // Apply search filter if there's a search query
      if (searchQuery && searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const matchesName = product.name.toLowerCase().includes(query);
        const matchesBrand = product.brand.toLowerCase().includes(query);
        const matchesColorway = product.colorway?.toLowerCase().includes(query) || false;
        
        // Only show products that match the search query
        if (!(matchesName || matchesBrand || matchesColorway)) {
          return false;
        }
      }
      
      // Apply other existing filters
      
      // Filter by brand
      if (selectedBrand !== 'All' && product.brand !== selectedBrand) {
        return false;
      }
      
      // Filter by category (size type)
      if (selectedCategory !== 'All') {
        const matchesCategory = 
          (product.sizeType && product.sizeType.includes(selectedCategory)) ||
          (product.category === selectedCategory);
        
        if (!matchesCategory) {
          return false;
        }
      }
      
      // Filter by specific size
      if (selectedSize !== 'All') {
        const hasSize = product.sizes.some(size => {
          return normalizeSizeDisplay(size) === normalizeSizeDisplay(selectedSize);
        });
        
        if (!hasSize) {
          return false;
        }
      }
      
      // Filter by size selections from URL
      if (selectedSizes.length > 0) {
        const hasAnySelectedSize = selectedSizes.some(selectedSize => {
          return product.sizes.some(productSize => {
            return normalizeSizeDisplay(productSize) === normalizeSizeDisplay(selectedSize);
          });
        });
        
        if (!hasAnySelectedSize) {
          return false;
        }
      }
      
      // Filter by price range
      if (product.price < minPrice || product.price > maxPrice) {
        return false;
      }
      
      // Filter by active filter type
      if (activeFilter === 'featured') {
        // Return featured products (assuming isFeatured flag)
        return product.isFeatured === true;
      } else if (activeFilter === 'new') {
        // Placeholder for new arrivals
        // In a real app, you would use createdAt date or a specific flag
        return true;
      }
      
      // Default: return all products that passed the filters
      return true;
    });
  };
  
  const handleLoadMore = () => {
    setVisibleProductCount(prev => prev + 8);
  };
  
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    
    // Scroll to the top of the container when filter is changed
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Alternative approach using offsetTop
      // window.scrollTo({
      //   top: containerRef.current.offsetTop - 100, // Subtract header height or desired offset
      //   behavior: 'smooth'
      // });
    }
  };
  
  const handleCategoryChange = (value: string) => {
    console.log(`Changing category to: ${value}`);
    
    // When category changes, we should reset size selection
    setSelectedSize('All');
    setSelectedSizes([]);
    
    // Set category after clearing size selections to prevent race conditions
    setSelectedCategory(value);
    
    // For URL parameters, preserve size selection if there's one from the URL
    const searchParams = new URLSearchParams(window.location.search);
    const sizeParam = searchParams.get('sizes');
    
    // If there's a size parameter, we'll let the URL parameter handler handle it
    // Otherwise, update the URL with just the category
    if (!sizeParam) {
      if (value !== 'All') {
        searchParams.set('category', value);
      } else {
        searchParams.delete('category');
      }
      
      // Preserve other parameters like brand
      const newUrl = searchParams.toString() 
        ? `/inventory?${searchParams.toString()}` 
        : '/inventory';
      window.history.pushState({}, '', newUrl);
    }
    
    // Log available sizes for new category after a short delay to ensure category is set
    setTimeout(() => {
      const sizesForCategory = getDynamicSizeOptions(value);
      console.log(`Available sizes for category ${value}:`, sizesForCategory);
    }, 50);
  };
  
  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = e.target.value;
    
    // Always allow setting the size regardless of other filters
    setSelectedSize(newSize);
    
    // Clear multi-selection
    setSelectedSizes([]);
    
    // Update URL parameters if a specific size is selected
    if (newSize !== 'All') {
      // Always apply the current category to the size for consistency
      let sizeValue = newSize;
      
      // Convert numeric size to proper format with suffix
      if (!sizeValue.match(/[a-zA-Z]/) && !isNaN(parseFloat(sizeValue))) {
        if (selectedCategory === 'M') {
          sizeValue = `${sizeValue}M`;
        } else if (selectedCategory === 'GS') {
          sizeValue = `${sizeValue}Y`;
        } else if (selectedCategory === 'W') {
          sizeValue = `${sizeValue}W`;
        } else {
          // Default to Men's if no specific category is selected
          sizeValue = `${sizeValue}M`;
        }
      }
      
      // Preserve existing query parameters
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sizes', sizeValue);
      
      const newUrl = `/inventory?${searchParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    } else {
      // Remove only the sizes parameter, keep other parameters
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('sizes');
      
      const newUrl = searchParams.toString() 
        ? `/inventory?${searchParams.toString()}` 
        : '/inventory';
      window.history.pushState({}, '', newUrl);
    }
  };
  
  const handlePriceChange = (min: number, max: number) => {
    setMinPrice(min);
    setMaxPrice(max);
  };

  // Refresh filter options whenever products change
  useEffect(() => {
    // Only update if we have products and we're not in a loading state
    if (products.length > 0 && !loading) {
      // Get the available brands based on current products
      generateBrandOptions();
      
      // Get available price ranges based on current products
      generatePriceRanges();
    }
  }, [products, loading]);

  // Helper function to display size range in a user-friendly way
  const getSizeRangeDisplay = (sizes: string[]): string => {
    // For single size, use direct format without range
    if (sizes.length === 1) {
      const size = sizes[0];
      
      // If it already has a suffix, just return it directly
      if (size.match(/[a-zA-Z]/)) {
        return size;
      }
      
      // Add appropriate suffix based on category
      if (selectedCategory === 'M') return `${size}M`;
      if (selectedCategory === 'W') return `${size}W`;
      if (selectedCategory === 'GS') return `${size}Y`;
      
      // Default to Men's if no category
      return `${size}M`;
    }
    
    // Check for known ranges
    if (sizes.some(s => s.includes('Y'))) {
      return "Grade School (3.5Y - 7Y)";
    }
    
    // Get min and max for M sizes
    const mSizes = sizes.filter(s => s.includes('M'));
    if (mSizes.length > 0) {
      // Extract numeric parts
      const numericSizes = mSizes.map(s => parseFloat(s.replace('M', '')));
      const min = Math.min(...numericSizes);
      const max = Math.max(...numericSizes);
      
      if (min === 8 && max === 8.5) return "Men's 8-8.5";
      if (min === 9 && max === 9.5) return "Men's 9-9.5";
      if (min === 10 && max === 10.5) return "Men's 10-10.5";
      if (min === 11 && max === 11.5) return "Men's 11-11.5";
      if (min === 12 && max === 12.5) return "Men's 12-12.5";
      if (min === 13 && max === 15) return "Men's 13-15";
      
      return `Men's ${min}-${max}`;
    }
    
    // Get sizes without suffix
    const numericSizes = sizes.filter(s => !isNaN(parseFloat(s)) && !s.match(/[a-zA-Z]/));
    if (numericSizes.length > 0) {
      // Treat as Men's sizes if category is M or All
      if (selectedCategory === 'M' || selectedCategory === 'All') {
        const nums = numericSizes.map(s => parseFloat(s));
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        return min === max ? `${min}M` : `Men's ${min}-${max}`;
      }
    }
    
    // Default to comma-separated list
    return sizes.join(', ');
  };

  // Update available size options when category changes
  useEffect(() => {
    // If products aren't loaded yet, wait
    if (loading || products.length === 0) return;
    
    console.log(`Category changed to: ${selectedCategory}, updating available sizes`);
    
    // Get the available sizes for this category
    const availableSizes = getDynamicSizeOptions(selectedCategory);
    console.log(`Available sizes for ${selectedCategory}:`, availableSizes);
    
    // If our current selected size isn't valid for the new category, reset it
    if (selectedSize !== 'All' && !availableSizes.includes(selectedSize)) {
      console.log(`Selected size ${selectedSize} not available in new category ${selectedCategory}, resetting to 'All'`);
      setSelectedSize('All');
    }
  }, [selectedCategory, products, loading]);

  // When rendering, show search results if there's a search query
  const renderHeading = () => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    
    if (searchQuery) {
      return (
        <div className="border-b border-gray-800 pb-5">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
            Search Results
          </h1>
          <p className="mt-2 max-w-4xl text-gray-400">
            Showing results for "{searchQuery}"
          </p>
        </div>
      );
    }
    
    return (
      <div className="border-b border-gray-800 pb-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
          Our Entire Inventory
        </h1>
        <p className="mt-2 max-w-4xl text-gray-400">
          Discover our entire inventory with rare, hard-to-find sneakers: Jordan Retros, Nike SBs, Collaborations, and more.
        </p>
      </div>
    );
  };

  // Add the handleBrandChange function
  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBrand(e.target.value);
  };

  // Force scroll to top on component mount
  useEffect(() => {
    // Immediately scroll to top with no animation to ensure the page starts at the top
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      
      <main className="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          {renderHeading()}
        </div>
        
        {/* Filters section */}
        <div className="mb-8">
          {/* Display active filters section */}
          {(selectedBrand !== 'All' || selectedCategory !== 'All' || selectedSize !== 'All' || selectedSizes.length > 0) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedBrand !== 'All' && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Brand: {selectedBrand}</span>
                  <button 
                    onClick={() => {
                      setSelectedBrand('All');
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear brand filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {selectedCategory !== 'All' && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Category: {standardCategories.find(cat => cat.id === selectedCategory)?.label.replace(' (M)', '').replace(' (W)', '').replace(' (GS)', '').replace(' (TD)', '') || selectedCategory}</span>
                  <button 
                    onClick={() => {
                      setSelectedCategory('All');
                      // Reset size when category is cleared
                      setSelectedSize('All');
                      setSelectedSizes([]);
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear category filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {(selectedSize !== 'All' || selectedSizes.length > 0) && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">
                    {selectedSizes.length > 0 ? (
                      <>Size: {getSizeRangeDisplay(selectedSizes)}</>
                    ) : selectedSize !== 'All' ? (
                      <>Size: {normalizeSizeDisplay(selectedSize)}</>
                    ) : null}
                  </span>
                  <button 
                    onClick={() => {
                      setSelectedSizes([]);
                      setSelectedSize('All');
                      // Clear size URL parameter
                      const searchParams = new URLSearchParams(window.location.search);
                      searchParams.delete('sizes');
                      const newUrl = searchParams.toString() 
                        ? `/inventory?${searchParams.toString()}` 
                        : '/inventory';
                      window.history.pushState({}, '', newUrl);
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear size filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {(minPrice > 0 || maxPrice < 15000) && (
                <div className="inline-flex items-center py-2 px-4 bg-accent/10 text-accent rounded-full text-sm font-medium">
                  <span className="mr-2">Price: ${minPrice} - ${maxPrice}</span>
                  <button 
                    onClick={() => {
                      setMinPrice(0);
                      setMaxPrice(15000);
                    }}
                    className="text-accent hover:text-white"
                    aria-label="Clear price filter"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <button
                onClick={() => {
                  setSelectedBrand('All');
                  setSelectedCategory('All');
                  setSelectedSize('All');
                  setSelectedSizes([]);
                  setMinPrice(0);
                  setMaxPrice(15000);
                  // Clear URL parameters
                  window.history.pushState({}, '', '/inventory');
                }}
                className="inline-flex items-center py-2 px-4 bg-red-500/10 text-red-400 rounded-full text-sm font-medium hover:bg-red-500/20"
                aria-label="Clear all filters"
              >
                <span>Clear All</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Mobile filter button - only visible on small screens */}
        <div className="sm:hidden mb-6">
          <button 
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-background-light rounded-md hover:bg-background-light/80 transition-colors"
          >
            <FiFilter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
        
        {/* Filter and product section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filter section - desktop */}
          <aside className="hidden sm:block lg:col-span-1 space-y-6">
            <div className="bg-background p-6 rounded-lg">
              <h2 className="text-lg font-medium mb-4">Filters</h2>
              
              {/* Brand filter */}
              <div className="mb-6">
                <label htmlFor="brand" className="block text-sm font-medium mb-2">Brand</label>
                <select
                  id="brand"
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  disabled={loading || products.length === 0}
                  className={`w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent ${loading ? 'opacity-60' : ''}`}
                >
                  {loading ? (
                    <option>Loading brands...</option>
                  ) : (
                    brands.map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.label}</option>
                    ))
                  )}
                </select>
              </div>
              
              {/* Category filter */}
              <CategoryFilter 
                value={selectedCategory}
                onChange={handleCategoryChange}
                categories={standardCategories}
                disabled={loading || products.length === 0}
                className={`mb-6 ${loading ? 'opacity-60' : ''}`}
              />
              
              {/* Size filter */}
              <div className="mb-6">
                <label htmlFor="size" className="block text-sm font-medium mb-2">Size</label>
                <select
                  id="size"
                  value={selectedSize}
                  onChange={handleSizeChange}
                  disabled={loading || products.length === 0}
                  className={`w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent ${loading ? 'opacity-60' : ''}`}
                >
                  {loading ? (
                    <option>Loading sizes...</option>
                  ) : (
                    getCurrentSizes().map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))
                  )}
                </select>
              </div>
              
              {/* Price range filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Price</label>
                <PriceRangeSlider
                  minValue={0}
                  maxValue={15000}
                  step={50}
                  onChange={handlePriceChange}
                  defaultMinPrice={minPrice}
                  defaultMaxPrice={maxPrice}
                />
              </div>
            </div>
          </aside>
          
          {/* Mobile filter drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 flex z-40 sm:hidden" aria-modal="true">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black/70 transition-opacity"
                aria-hidden="true"
                onClick={() => setMobileFiltersOpen(false)}
              ></div>
              
              {/* Sliding panel */}
              <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-background px-4 py-6 shadow-xl">
                {/* Close button */}
                <div className="flex items-center justify-between mb-6 px-2">
                  <h2 className="text-lg font-medium">Filters</h2>
                  <button
                    type="button"
                    className="p-2 text-secondary hover:text-primary"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Filter content - mobile */}
                <div className="space-y-6">
                  {/* Brand filter */}
                  <div>
                    <label htmlFor="mobile-brand" className="block text-sm font-medium mb-2">Brand</label>
                    <select
                      id="mobile-brand"
                      value={selectedBrand}
                      onChange={handleBrandChange}
                      disabled={loading || products.length === 0}
                      className={`w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent ${loading ? 'opacity-60' : ''}`}
                    >
                      {loading ? (
                        <option>Loading brands...</option>
                      ) : (
                        brands.map(brand => (
                          <option key={brand.id} value={brand.id}>{brand.label}</option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  {/* Category filter */}
                  <div>
                    <label htmlFor="mobile-category" className="block text-sm font-medium mb-2">Size Category</label>
                    <select
                      id="mobile-category"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={loading || products.length === 0}
                      className={`w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent ${loading ? 'opacity-60' : ''}`}
                    >
                      {loading ? (
                        <option>Loading categories...</option>
                      ) : (
                        standardCategories.map(category => (
                          <option key={category.id} value={category.id}>{category.label}</option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  {/* Size filter */}
                  <div>
                    <label htmlFor="mobile-size" className="block text-sm font-medium mb-2">Size</label>
                    <select
                      id="mobile-size"
                      value={selectedSize}
                      onChange={handleSizeChange}
                      disabled={loading || products.length === 0}
                      className={`w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent ${loading ? 'opacity-60' : ''}`}
                    >
                      {loading ? (
                        <option>Loading sizes...</option>
                      ) : (
                        getCurrentSizes().map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  {/* Price range filter */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Price</label>
                    <PriceRangeSlider
                      minValue={0}
                      maxValue={15000}
                      step={50}
                      onChange={handlePriceChange}
                      defaultMinPrice={minPrice}
                      defaultMaxPrice={maxPrice}
                    />
                  </div>
                  
                  {/* Apply filters button */}
                  <div className="mt-8">
                    <button
                      className="w-full bg-accent text-white py-3 px-4 rounded-md hover:bg-accent/90 transition-colors"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Products grid */}
          <div className="lg:col-span-3" ref={containerRef}>
            {/* Filter tabs */}
            <div className="mb-6 flex flex-wrap gap-2 items-center overflow-x-auto pb-1">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeFilter === filter.id 
                      ? 'bg-accent text-white' 
                      : 'bg-background-light text-secondary hover:text-primary'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            
            {/* Product listing section */}
            <section className="mt-8">
              {/* Store filtered products in a variable to avoid multiple calls */}
              {(() => {
                const filteredProducts = getFilteredProducts();
                
                return (
                  <>
                    {/* Loading state */}
                    {loading && (
                      <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                        <span className="ml-3 text-lg">Loading products...</span>
                      </div>
                    )}
                    
                    {/* Error state */}
                    {!loading && error && (
                      <div className="bg-red-900/20 rounded-lg p-10 text-center my-8">
                        <div className="text-red-400 mb-3">{error}</div>
                        <button
                          onClick={() => window.location.reload()}
                          className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition-colors"
                        >
                          Try Again
                        </button>
                      </div>
                    )}
                    
                    {/* No products state */}
                    {!loading && !error && products.length === 0 && (
                      <div className="bg-background-light/20 rounded-lg p-10 text-center my-8">
                        <div className="text-secondary mb-3">No products available at this time.</div>
                        <p className="text-gray-400">Please check back later for new products.</p>
                      </div>
                    )}
                    
                    {/* No results - only show when not loading */}
                    {!loading && !error && products.length > 0 && filteredProducts.length === 0 && (
                      <div className="bg-background-light/20 rounded-lg p-8 text-center my-8">
                        <div className="text-secondary mb-3">No products found matching your filters.</div>
                        <button
                          onClick={() => {
                            setActiveFilter('all');
                            setSelectedBrand('All');
                            setSelectedCategory('All');
                            setSelectedSize('All');
                            setMinPrice(0);
                            setMaxPrice(15000);
                          }}
                          className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition-colors"
                        >
                          Reset Filters
                        </button>
                      </div>
                    )}
                    
                    {/* Products grid */}
                    {!loading && !error && filteredProducts.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6">
                        {filteredProducts.slice(0, visibleProductCount).map((product) => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                      </div>
                    )}
                    
                    {/* Load more button */}
                    {!loading && !error && filteredProducts.length > visibleProductCount && (
                      <div className="flex justify-center mt-12">
                        <button
                          onClick={handleLoadMore}
                          className="px-6 py-3 bg-accent hover:bg-accent-dark text-white rounded-md transition-colors"
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default EntireInventoryPage; 